import { useEffect, useState } from "react";
import { lookupUser, UserLookupResult } from "../api/userLookupApi";

/**
 * Live-preview state shape returned by the hook. `status` drives the UI:
 *
 *   - "idle"      : input is empty or below threshold; show nothing
 *   - "loading"   : a debounced request is in flight; show a subtle spinner/“…”
 *   - "found"     : Graph returned a user; render displayName + accountEnabled
 *   - "not_found" : Graph returned 404 → invalid UPN; show a red note
 *   - "malformed" : input doesn't look like an email at all; show a hint
 *                  (kept client-side so we don't burn an API call on it)
 *   - "error"     : network / server error; show errorMessage
 *
 * `result` holds the backend payload on a successful "found" lookup so the
 * caller can render displayName, accountEnabled, slackId, etc. It's null in
 * every other state.
 */
export type LookupStatus =
  | "idle"
  | "malformed"
  | "loading"
  | "found"
  | "not_found"
  | "error";

export interface LookupState {
  status: LookupStatus;
  /** Populated only when status === "found". */
  result: UserLookupResult | null;
  /** Populated only when status === "error" (network / 5xx / 400). */
  errorMessage: string | null;
}

/**
 * Same loose email-shape check used by the backend (manageUsersLookup.ts /
 * adminToolsApi.ts). Mirrored here so we don't fire a request on obviously
 * malformed input — the live preview just stays in the "malformed" state.
 */
const UPN_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Debounces an in-flight lookup of `upn` via GET /api/manage/users/lookup.
 *
 * Behavior:
 *   - Empty / whitespace-only input → status="idle", no request
 *   - Input fails shape check       → status="malformed", no request
 *   - Otherwise after `debounceMs`  → status="loading" → request → resolve
 *
 * Re-typing during the debounce window cancels the pending timer cleanly
 * (effect cleanup), and a stale-response guard skips updates from previous
 * requests that resolve after the user has typed something newer.
 *
 * Used by:
 *   - AdminToolsPage's override-UPN field (Phase 4.7 Chunk F)
 *   - AdminCycleDetailPage's reassign-S2 modal (Phase 4.7 Chunk G)
 */
export function useDebouncedUserLookup(upn: string, debounceMs = 300): LookupState {
  const [state, setState] = useState<LookupState>({
    status: "idle",
    result: null,
    errorMessage: null,
  });

  useEffect(() => {
    const trimmed = upn.trim();

    if (trimmed.length === 0) {
      setState({ status: "idle", result: null, errorMessage: null });
      return;
    }

    if (!UPN_PATTERN.test(trimmed)) {
      setState({ status: "malformed", result: null, errorMessage: null });
      return;
    }

    // Stale-response guard. The closure captures this flag; the cleanup
    // function sets it before the next effect run, so an in-flight request
    // that resolves AFTER the user re-types won't overwrite the newer state.
    let cancelled = false;

    setState({ status: "loading", result: null, errorMessage: null });

    const timer = setTimeout(async () => {
      try {
        const result = await lookupUser(trimmed);
        if (cancelled) return;
        if (result.exists) {
          setState({ status: "found", result, errorMessage: null });
        } else {
          setState({ status: "not_found", result: null, errorMessage: null });
        }
      } catch (err: unknown) {
        if (cancelled) return;
        // axios-shaped error or generic Error; surface whatever message we
        // can. The backend's 400 path returns {error: "..."} which the
        // caller can inspect via the server message.
        const anyErr = err as { response?: { data?: { error?: string } }; message?: string };
        const serverMsg = anyErr?.response?.data?.error;
        const message = serverMsg ?? anyErr?.message ?? "Lookup failed.";
        setState({ status: "error", result: null, errorMessage: message });
      }
    }, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [upn, debounceMs]);

  return state;
}
